/**
 * D4 — 잡음 바닥 추정 훅(호스트 화면 전용).
 *
 * 왜 `CaptureStats` 가 아니라 훅인가: 추정기는 창·지속시간이라는 **시간
 * 상태**를 갖고, `MicCapture` 는 `onStats` 를 ~16 fps 로 쏜다. 캡처에 넣으면
 * 시간 상태가 stats 경로에 끼어들고, `CaptureStats` 는 테스트 더블이 손으로
 * 채우는 구조라 더블이 거짓말을 한다. 값이 서버로 가지 않으므로 계약 영향 0.
 *
 * 400 ms 타이머로 도는 이유는 `LevelMeter` 와 같다: 완전 무음에서 값이 안
 * 바뀌어도 **시간은 흘러야** 지속 조건이 판정된다. 단 타이머는 **표본을
 * 적립하지 않는다**(F4) — 같은 `dbfs` 를 매 주기 새 표본으로 복제하면 새
 * 오디오 블록이 0 건인데도 최소 표본 40·지속 10 s 가 채워져 "표본 부족 시
 * 경고 금지" 불변식이 무너진다. 표본은 `frameCount` 가 **변했을 때만** 쌓는다.
 *
 * `await import()` 를 쓰지 않는다 — vitest 의 mocked dynamic import 는 두
 * 번째 await 에서 resolve 되지 않아 StrictMode 검증이 통째로 막힌다.
 */

import { useEffect, useRef, useState } from 'react'
import {
  initialNoiseFloorState,
  pushNoiseSample,
  type NoiseFloorState,
} from './noise-floor'

export function useNoiseFloor(
  dbfs: number,
  active: boolean,
  /**
   * 캡처가 지금까지 **만든** 프레임의 단조 카운터(`CaptureStats.framesGenerated`).
   * 이 값이 변했을 때만 새 표본이다 — 타이머만 도는 구간은 새 오디오가
   * 아니다(F4). 전송 결과가 아니라 **생성**을 세므로 단절 보류 중에도
   * 표본이 계속 쌓인다(R2).
   */
  frameCount: number,
): { floorDbfs: number | null; warn: boolean } {
  const [state, setState] = useState<NoiseFloorState>(initialNoiseFloorState)
  // StrictMode 이중 마운트에서도 같은 프레임을 두 번 적립하지 않게 ref 로
  // 둔다(마운트마다 새로 만들어지는 값이면 중복 적립이 된다).
  const lastCounted = useRef<number | null>(null)

  useEffect(() => {
    const tick = (): void => {
      // 같은 프레임 카운터로는 **한 번만** 적립한다.
      const fresh = lastCounted.current !== frameCount
      if (fresh) lastCounted.current = frameCount
      setState((prev) =>
        pushNoiseSample(
          prev,
          Date.now(),
          active,
          fresh ? { dbfs } : undefined,
        ),
      )
    }
    tick()
    const timer = setInterval(tick, 400)
    return () => clearInterval(timer)
  }, [dbfs, active, frameCount])

  return { floorDbfs: state.floorDbfs, warn: state.warn }
}
